import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useDhsFlag } from 'utils/featureFlags';
import {
  DEFAULT_ACTIVE_HURRICANES_SOURCE,
  DEFAULT_TROPICAL_CONDITIONS,
  useCreateOrgEventAutomationRule,
  useOrgEventAutomationRules,
  type CreateOrgEventAutomationRulePayload,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { NotificationType } from 'hooks/useNotification';
import {
  DHS_REGION4_AUTOMATION_RULE_NAME,
  DHS_REGION4_FLOOD_RULE_NAME,
  DHS_REGION4_INFRASTRUCTURE_RULE_NAME,
  DHS_REGION4_WILDFIRE_RULE_NAME,
} from '../NotificationsTab/dhsRegion4ThreatNotification';

const ensureLocks = new Map<string, Promise<unknown>>();

const DHS_SEEDED_RULES: Array<{
  name: string;
  type: string;
  thresholdDescription: string;
}> = [
  {
    name: DHS_REGION4_AUTOMATION_RULE_NAME,
    type: 'Tropical Storm',
    thresholdDescription:
      'Storm category ≥ Tropical Storm (TS) and ≥ 1 DHS facility in the Helene forecast cone',
  },
  {
    name: DHS_REGION4_FLOOD_RULE_NAME,
    type: 'Flood',
    thresholdDescription: 'Flood risk to DHS Region 4 facilities',
  },
  {
    name: DHS_REGION4_WILDFIRE_RULE_NAME,
    type: 'Wildfire',
    thresholdDescription: 'Wildfire / smoke risk to DHS Region 4 facilities',
  },
  {
    name: DHS_REGION4_INFRASTRUCTURE_RULE_NAME,
    type: 'Infrastructure',
    thresholdDescription:
      'Infrastructure disruption risk to DHS Region 4 facilities',
  },
];

/**
 * Ensures the DHS group has tropical / flood / wildfire / infrastructure rules
 * so resource checklists can be keyed by rule name.
 */
export function useEnsureDhsTropicalAutomationRule() {
  const dhsEnabled = useDhsFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const { data: rules, isSuccess } = useOrgEventAutomationRules({
    enabled: dhsEnabled && !!groupGuid,
  });
  const createRule = useCreateOrgEventAutomationRule();
  const attempted = useRef(false);

  useEffect(() => {
    if (!dhsEnabled || !groupGuid || !isSuccess || attempted.current) return;
    const existingNames = new Set(
      (rules ?? []).filter((rule) => !rule.archived).map((rule) => rule.name)
    );
    const missing = DHS_SEEDED_RULES.filter(
      (seed) => !existingNames.has(seed.name)
    );
    if (missing.length === 0) {
      attempted.current = true;
      return;
    }

    const lockKey = `dhs-automation-rules:${groupGuid}`;
    if (ensureLocks.has(lockKey)) return;

    attempted.current = true;
    const notifyConfig = {
      notificationTypes: [NotificationType.IN_APP],
      userIds: userGuid ? [userGuid] : [],
      teamIds: [],
      priority: 'watch',
      titleTemplate: 'Threshold event: {eventName}',
      bodyTemplate:
        '{eventName}: {stormName} impacts {resourceCount} resources.',
    };

    const promise = Promise.all(
      missing.map((seed) => {
        const payload: CreateOrgEventAutomationRulePayload = {
          group_guid: groupGuid,
          name: seed.name,
          enabled: false,
          logic: 'all',
          source: DEFAULT_ACTIVE_HURRICANES_SOURCE,
          conditions: DEFAULT_TROPICAL_CONDITIONS,
          event_template: {
            severity: 'High',
            type: seed.type,
            region: 'FEMA Region IV',
            thresholdDescription: seed.thresholdDescription,
          },
          notify_on_create: false,
          checklist_on_create: false,
          checklist_config: { items: [] },
          notify_config: notifyConfig,
          created_by_user_guid: userGuid ?? null,
        };
        return createRule.mutateAsync(payload);
      })
    )
      .catch((err) => {
        console.warn('[dhs] failed to ensure automation rules', err);
        attempted.current = false;
      })
      .finally(() => {
        ensureLocks.delete(lockKey);
      });

    ensureLocks.set(lockKey, promise);
  }, [dhsEnabled, groupGuid, userGuid, isSuccess, rules, createRule]);
}
